// ============================================================================
// EMISSIONS API CLIENT
// ============================================================================
// Client-side API calls for the carbon emissions calculation system
// ============================================================================

import { ApiClient } from '../../../core/apiClient';

const BASE = '/emissions';

class EmissionsApi extends ApiClient {
  constructor() {
    super({ enableLogging: import.meta.env.DEV });
  }

  // ── Emission Factors ────────────────────────────────────────────────────────

  async getFactors(query: {
    scope?: string;
    region?: string;
    country_code?: string;
    is_system?: boolean;
    category?: string;
    search?: string;
  } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (query.scope) params.append('scope', query.scope);
    if (query.region) params.append('region', query.region);
    if (query.country_code) params.append('country_code', query.country_code);
    if (query.is_system !== undefined) params.append('is_system', String(query.is_system));
    if (query.category) params.append('category', query.category);
    if (query.search) params.append('search', query.search);
    const qs = params.toString();
    const url = qs ? `${BASE}/factors?${qs}` : `${BASE}/factors`;
    const response = await this.client.get<any>(url);
    return response.data?.data ?? response.data ?? [];
  }

  async getSystemFactors(): Promise<any[]> {
    const response = await this.client.get<any>(`${BASE}/factors/system`);
    return response.data?.data ?? response.data ?? [];
  }

  async getGwpStandards(): Promise<any[]> {
    const response = await this.client.get<any>(`${BASE}/gwp-standards`);
    return response.data?.data ?? response.data ?? [];
  }

  async getScope3Categories(): Promise<any[]> {
    const response = await this.client.get<any>(`${BASE}/scope3-categories`);
    return response.data?.data ?? response.data ?? [];
  }

  async getUnitConversions(): Promise<any[]> {
    const response = await this.client.get<any>(`${BASE}/unit-conversions`);
    return response.data?.data ?? response.data ?? [];
  }

  // ── Calculate ────────────────────────────────────────────────────────────────

  async calculate(data: {
    emission_factor_id: number;
    activity_amount: number;
    input_unit: string;
    gwp_standard?: string;
    company_id?: number;
  }): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/calculate`, data);
    return response.data?.data ?? response.data;
  }

  // ── Bulk Sessions ─────────────────────────────────────────────────────────────

  async getBulkSessions(): Promise<any[]> {
    const response = await this.client.get<any>(`${BASE}/bulk/sessions`);
    return response.data?.data ?? response.data ?? [];
  }

  async createBulkSession(data: {
    company_id: number;
    session_name: string;
    description?: string;
    gwp_standard?: string;
    scope2_method?: string;
  }): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/bulk/sessions`, data);
    return response.data?.data ?? response.data;
  }

  async getBulkSession(id: number | string): Promise<any> {
    const response = await this.client.get<any>(`${BASE}/bulk/sessions/${id}`);
    return response.data?.data ?? response.data;
  }

  async deleteBulkSession(id: number | string): Promise<void> {
    await this.client.delete(`${BASE}/bulk/sessions/${id}`);
  }

  async addBulkRow(sessionId: number | string, data: {
    row_number: number;
    activity_description: string;
    emission_factor_id?: number;
    activity_amount: number;
    input_unit: string;
    scope?: string;
    scope3_category?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/bulk/sessions/${sessionId}/rows`, data);
    return response.data?.data ?? response.data;
  }

  async processBulkSession(id: number | string): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/bulk/sessions/${id}/process`, {});
    return response.data?.data ?? response.data;
  }

  // ── Targets ───────────────────────────────────────────────────────────────────

  async getTargets(): Promise<any[]> {
    const response = await this.client.get<any>(`${BASE}/targets`);
    return response.data?.data ?? response.data ?? [];
  }

  async createTarget(data: {
    name: string;
    target_type: string;
    scope?: string;
    baseline_year: number;
    target_year: number;
    baseline_value: number;
    target_value: number;
    intensity_metric?: string;
  }): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/targets`, data);
    return response.data?.data ?? response.data;
  }

  async getTargetProgress(id: number | string): Promise<any> {
    const response = await this.client.get<any>(`${BASE}/targets/${id}/progress`);
    return response.data?.data ?? response.data;
  }

  async updateTargetProgress(id: number | string, current_value: number): Promise<any> {
    const response = await this.client.put<any>(`${BASE}/targets/${id}/progress`, { current_value });
    return response.data?.data ?? response.data;
  }

  async deleteTarget(id: number | string): Promise<void> {
    await this.client.delete(`${BASE}/targets/${id}`);
  }

  // ── Offsets ───────────────────────────────────────────────────────────────────

  async getOffsets(): Promise<any[]> {
    const response = await this.client.get<any>(`${BASE}/offsets`);
    return response.data?.data ?? response.data ?? [];
  }

  async createOffset(data: {
    name: string;
    registry: string;
    project_id?: string;
    project_type?: string;
    vintage_year: number;
    quantity_tco2e: number;
    price_per_tco2e?: number;
    country?: string;
    verification_status?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/offsets`, data);
    return response.data?.data ?? response.data;
  }

  async getOffsetStats(): Promise<any> {
    const response = await this.client.get<any>(`${BASE}/offsets/stats`);
    return response.data?.data ?? response.data;
  }

  async deleteOffset(id: number | string): Promise<void> {
    await this.client.delete(`${BASE}/offsets/${id}`);
  }

  // ── Intensity ─────────────────────────────────────────────────────────────────

  async getIntensity(query: {
    period_start: string;
    period_end: string;
    denominator_type: string;
    denominator_value: number;
  }): Promise<any> {
    const params = new URLSearchParams();
    params.append('period_start', query.period_start);
    params.append('period_end', query.period_end);
    params.append('denominator_type', query.denominator_type);
    params.append('denominator_value', String(query.denominator_value));
    const response = await this.client.get<any>(`${BASE}/intensity?${params}`);
    return response.data?.data ?? response.data;
  }

  // ── Reports ───────────────────────────────────────────────────────────────────

  async getReports(): Promise<any[]> {
    const response = await this.client.get<any>(`${BASE}/reports`);
    return response.data?.data ?? response.data ?? [];
  }

  async generateReport(data: {
    name: string;
    report_type: string;
    period_start: string;
    period_end: string;
    gwp_standard?: string;
  }): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/reports`, data);
    return response.data?.data ?? response.data;
  }

  async downloadReport(id: number | string): Promise<Blob> {
    const response = await this.client.get(`${BASE}/reports/${id}/download`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async deleteReport(id: number | string): Promise<void> {
    await this.client.delete(`${BASE}/reports/${id}`);
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────────

  async getDashboard(params: { company_id?: number } = {}): Promise<any> {
    const qs = params.company_id ? `?company_id=${params.company_id}` : '';
    const response = await this.client.get<any>(`${BASE}/dashboard${qs}`);
    return response.data?.data ?? response.data;
  }

  // ── Spend-Based (EEIO) Calculations ──────────────────────────────────────────

  async getEeioSectors(search?: string): Promise<any[]> {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await this.client.get<any>(`${BASE}/spend/sectors${qs}`);
    return response.data?.data ?? response.data ?? [];
  }

  async calculateSpendBased(data: {
    sector_code: string;
    amount: number;
    currency: 'GBP' | 'USD' | 'EUR';
  }): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/spend/calculate`, data);
    return response.data?.data ?? response.data;
  }

  async calculateSpendBatch(items: Array<{
    sector_code: string;
    amount: number;
    currency: string;
  }>): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/spend/calculate-batch`, { items });
    return response.data?.data ?? response.data;
  }

  // ── AI Factor Matching ──────────────────────────────────────────────────────

  async matchFactor(data: {
    description: string;
    scope?: string;
    region?: string;
    unit?: string;
    limit?: number;
  }): Promise<any[]> {
    const response = await this.client.post<any>(`${BASE}/match`, data);
    return response.data?.data ?? response.data ?? [];
  }

  async matchFactorBatch(activities: Array<{
    description: string;
    scope?: string;
    unit?: string;
  }>, region?: string): Promise<any[]> {
    const response = await this.client.post<any>(`${BASE}/match-batch`, { activities, region });
    return response.data?.data ?? response.data ?? [];
  }

  // ── Supplier Emissions ──────────────────────────────────────────────────────

  async getSupplierRequests(status?: string): Promise<any[]> {
    const qs = status ? `?status=${status}` : '';
    const response = await this.client.get<any>(`${BASE}/suppliers/requests${qs}`);
    return response.data?.data ?? response.data ?? [];
  }

  async createSupplierRequest(data: {
    supplier_name: string;
    supplier_email: string;
    supplier_contact?: string;
    scope3_category?: number;
    request_type?: string;
    due_date?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/suppliers/requests`, data);
    return response.data?.data ?? response.data;
  }

  async getSupplierRequest(id: number | string): Promise<any> {
    const response = await this.client.get<any>(`${BASE}/suppliers/requests/${id}`);
    return response.data?.data ?? response.data;
  }

  async updateSupplierRequestStatus(id: number | string, status: string): Promise<any> {
    const response = await this.client.put<any>(`${BASE}/suppliers/requests/${id}/status`, { status });
    return response.data?.data ?? response.data;
  }

  async deleteSupplierRequest(id: number | string): Promise<void> {
    await this.client.delete(`${BASE}/suppliers/requests/${id}`);
  }

  async verifySupplierResponse(responseId: number | string): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/suppliers/responses/${responseId}/verify`, {});
    return response.data?.data ?? response.data;
  }

  async getSupplierStats(): Promise<any> {
    const response = await this.client.get<any>(`${BASE}/suppliers/stats`);
    return response.data?.data ?? response.data;
  }

  // ── Industry Benchmarks ─────────────────────────────────────────────────────

  async getBenchmarks(filters: {
    region?: string;
    year?: number;
    source?: string;
    search?: string;
  } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.region) params.append('region', filters.region);
    if (filters.year) params.append('year', String(filters.year));
    if (filters.source) params.append('source', filters.source);
    if (filters.search) params.append('search', filters.search);
    const qs = params.toString();
    const url = qs ? `${BASE}/benchmarks?${qs}` : `${BASE}/benchmarks`;
    const response = await this.client.get<any>(url);
    return response.data?.data ?? response.data ?? [];
  }

  async compareToBenchmark(data: {
    industry_code: string;
    revenue?: number;
    employees?: number;
    period_start?: string;
    period_end?: string;
  }): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/benchmarks/compare`, data);
    return response.data?.data ?? response.data;
  }

  // ── Reporting Framework Templates ───────────────────────────────────────────

  async getReportTemplates(framework?: string): Promise<any[]> {
    const qs = framework ? `?framework=${framework}` : '';
    const response = await this.client.get<any>(`${BASE}/templates${qs}`);
    return response.data?.data ?? response.data ?? [];
  }

  async getReportTemplate(id: number | string): Promise<any> {
    const response = await this.client.get<any>(`${BASE}/templates/${id}`);
    return response.data?.data ?? response.data;
  }

  async getSubmissions(status?: string): Promise<any[]> {
    const qs = status ? `?status=${status}` : '';
    const response = await this.client.get<any>(`${BASE}/submissions${qs}`);
    return response.data?.data ?? response.data ?? [];
  }

  async createSubmission(data: {
    template_id: number;
    reporting_year: number;
    notes?: string;
  }): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/submissions`, data);
    return response.data?.data ?? response.data;
  }

  async getSubmission(id: number | string): Promise<any> {
    const response = await this.client.get<any>(`${BASE}/submissions/${id}`);
    return response.data?.data ?? response.data;
  }

  async updateSubmission(id: number | string, data: any, status?: string): Promise<any> {
    const response = await this.client.put<any>(`${BASE}/submissions/${id}`, { data, status });
    return response.data?.data ?? response.data;
  }

  async autoPopulateSubmission(id: number | string): Promise<any> {
    const response = await this.client.post<any>(`${BASE}/submissions/${id}/auto-populate`, {});
    return response.data?.data ?? response.data;
  }

  async deleteSubmission(id: number | string): Promise<void> {
    await this.client.delete(`${BASE}/submissions/${id}`);
  }
}

export const emissionsApi = new EmissionsApi();
